import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  Modal,
  KeyboardAvoidingView,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import ColumnsOnScreenModalStyles from './ColumnsOnScreenModal.styles';
import { useColors } from '../../../hooks/useColors';
import theme from '../../../common/theme';
import { DOCKET_WIDTH } from '../../../common/constants';
import { useScale } from '../../../hooks/useScale';

export interface ColumnsOnScreenModalProps {
  visible: boolean;
  onClose?: () => void | Promise<void>;
  onSave: (text: number) => void;
  currentColumns: number;
}

const ColumnsOnScreenModal: React.FC<ColumnsOnScreenModalProps> = ({
  visible,
  onClose = () => undefined,
  onSave,
  currentColumns = 8,
}) => {
  const { translate } = useTranslation();
  const styles = ColumnsOnScreenModalStyles();
  const { colors } = useColors();
  const [columns, setColumns] = useState<number>(currentColumns);
  const { scale } = useScale();
  const onPressSave = useCallback(() => {
    onSave(columns);
  }, [onSave, columns]);

  const { vw } = theme.window;

  const maxSupportedColumns = useMemo(() => {
    return Math.floor(vw / scale(DOCKET_WIDTH));
  }, [vw, scale]);

  const onColumnsChange = useCallback((value: string) => {
    if (!value) {
      setColumns(0);
      return;
    }
    setColumns(parseInt(value));
  }, []);

  return (
    <Modal
      animationType="fade"
      onDismiss={onClose}
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      transparent={true}
      visible={visible}
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
    >
      <KeyboardAvoidingView style={styles.background}>
        <View style={styles.container}>
          <Text style={styles.title}>
            {translate('kitchenDisplay.columnsOnScreen')}
          </Text>
          <View style={styles.section}>
            <TextInput
              onChangeText={onColumnsChange}
              value={columns.toString()}
              style={styles.input}
              multiline
            />
            <Text style={styles.noteText}>
              {translate('kitchenDisplay.columnPerScreenNotes', {
                maxSupportedColumns,
              })}
            </Text>
          </View>
          <View style={styles.section}>
            <View style={styles.action}>
              <TouchableOpacity style={styles.btn} onPress={onClose}>
                <Text style={styles.btnText}>
                  {translate('button.dismiss')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.btn,
                  {
                    backgroundColor:
                      columns <= 0 ? colors.background : colors.green,
                  },
                ]}
                onPress={onPressSave}
                disabled={columns <= 0}
              >
                <Text style={styles.btnText}>
                  {translate('button.confirm')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default ColumnsOnScreenModal;
